import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Space,
    Popconfirm,
    message,
    Card,
    Row,
    Col,
    Tag,
    Collapse,
    InputNumber,
    Typography,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SaveOutlined,
    CloseOutlined,
    DownOutlined,
    FunctionOutlined,
} from '@ant-design/icons';
import userService from '@/services/userService';
import ownerService from '@/services/ownerService';

const { Option } = Select;
const { Panel } = Collapse;
const { Text, Title } = Typography;

const ValidationRulesManagerSimple = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [validationType, setValidationType] = useState(null);
    const [shipOptions, setShipOptions] = useState([]);

    // Reference data
    const modules = [
        { value: 'Position', label: 'Position' },
        { value: 'Tablename1', label: 'Module 1' },
        { value: 'Tablename2', label: 'Module 2' },
    ];

    const validationTypes = [
        { value: 'required', label: 'Required' },
        { value: 'min', label: 'Min' },
        { value: 'max', label: 'Max' },
        { value: 'range', label: 'Range' },
    ];

    const fieldTypes = [
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
    ];

    const fetchAllships = async () => {
        try {
            const getownerships = await ownerService.viewAllShips();
            const ships = getownerships.data?.map(x => ({
                label: x?.name,
                value: x?.imo_number
            })) || [];
            setShipOptions(ships);
        } catch (error) {
            console.error('Error fetching ships:', error);
        }
    }

    useEffect(() => {
        fetchValidationRules();
        fetchAllships();
    }, []);

    const fetchValidationRules = async () => {
        setLoading(true);
        try {
            const res = await userService.getcustomModuleSpecific();
            console.log('API Response:', res);

            if (res?.data && Array.isArray(res.data)) {
                // Transform the nested structure into flat table rows
                const transformedData = [];

                res?.data.forEach((item) => {
                    if (item.rules && Array.isArray(item.rules)) {
                        item.rules.forEach((rule) => {
                            transformedData.push({
                                id: rule.id,
                                table_name: item.moduleName,
                                field_name: item.fieldName,
                                field_type: item.fieldType,
                                validation_type: item.validationType,
                                formula: item.formula,
                                ship_id: rule.ship_id,
                                validation_parameters: rule.validation_parameters,
                                errormessage: rule.errormessage,
                                updatedAt: rule.updatedAt,
                                isRequired: rule.isRequired || false
                            });
                        });
                    }
                });

                console.log('Transformed Data:', transformedData);
                setData(transformedData);
                message.success(`Loaded ${transformedData.length} validation rules`);
            } else {
                setData([]);
                message.warning("No rules found");
            }
        } catch (error) {
            console.error('Fetch error:', error);
            message.error("Failed to fetch validation rules");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const groupDataByModule = () => {
        const grouped = {};
        data.forEach((item) => {
            const moduleName = item.table_name;
            if (!grouped[moduleName]) {
                grouped[moduleName] = [];
            }
            grouped[moduleName].push(item);
        });
        return grouped;
    };

    const cleanFormula = (formula) => {
        if (!formula || formula === 'null' || formula === null) return '';
        return String(formula)
            .replace(/^"+|"+$/g, "")
            .replace(/\\"/g, '"')
            .replace(/\\+$/g, "")
            .trim();
    };

    const handleAdd = (tableName = null) => {
        setEditingRecord(null);
        setSelectedModule(tableName);
        setValidationType(null);
        form.resetFields();
        if (tableName) {
            form.setFieldsValue({ table_name: tableName });
        }
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        console.log("Editing record:", record);
        setEditingRecord(record);

        // Parse validation_parameters
        let parsedParams = {};
        try {
            parsedParams = typeof record.validation_parameters === "string"
                ? JSON.parse(record.validation_parameters)
                : record.validation_parameters || {};
        } catch {
            parsedParams = {};
        }

        // Detect validation type
        let detectedType = record.validation_type;
        if (parsedParams.min !== undefined && parsedParams.max !== undefined) {
            detectedType = "range";
        } else if (parsedParams.min !== undefined) {
            detectedType = "min";
        } else if (parsedParams.max !== undefined) {
            detectedType = "max";
        }

        setValidationType(detectedType);
        setSelectedModule(record.table_name);

        // IMPORTANT: Convert ship_id to array format for multi-select
        const shipIdsArray = Array.isArray(record.ship_id)
            ? record.ship_id
            : [record.ship_id];

        console.log('Setting form values:', {
            table_name: record.table_name,
            field_name: record.field_name,
            field_type: record.field_type,
            validation_type: detectedType,
            ship_ids: shipIdsArray,
            minValue: parsedParams.min,
            maxValue: parsedParams.max,
            formula: cleanFormula(record.formula),
            errormessage: record.errormessage,
            isRequired: record.isRequired || false
        });

        // Set form values with a slight delay to ensure form is ready
        setTimeout(() => {
            form.setFieldsValue({
                table_name: record.table_name,
                field_name: record.field_name,
                field_type: record.field_type,
                validation_type: detectedType,
                ship_ids: shipIdsArray,
                minValue: parsedParams.min,
                maxValue: parsedParams.max,
                formula: cleanFormula(record.formula),
                errormessage: record.errormessage,
                isRequired: record.isRequired || false
            });
        }, 0);

        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            // Add your delete API call here if needed
            // await userService.deleteValidationRule(id);

            setData(data.filter((item) => item.id !== id));
            message.success('Validation rule deleted successfully');
        } catch (error) {
            console.error('Delete error:', error);
            message.error('Failed to delete validation rule');
        }
    };

    const handleSubmit = async (values) => {
        try {
            console.log('Form values submitted:', values);

            const shipIds = values.ship_ids || [];
            if (shipIds.length === 0) {
                message.error("Please select at least one ship");
                return;
            }

            const baseRule = {
                moduleName: values.table_name,
                fieldName: values.field_name,
                field_type: values.field_type,
                validation_type: values.validation_type,
                errormessage: values.errormessage,
                use_previous: values.use_previous || false,
                isRequired: values.isRequired || false,
                formula: values.formula || null,
                validation_parameters:
                    values.validation_type === "min"
                        ? { min: values.minValue }
                        : values.validation_type === "max"
                            ? { max: values.maxValue }
                            : values.validation_type === "range"
                                ? { min: values.minValue, max: values.maxValue }
                                : {},
            };

            let payloadArray = [];

            // Edit mode
            if (editingRecord) {
                payloadArray = shipIds.map((shipId) => ({
                    ship_id: shipId,
                    validation_obj: [
                        {
                            ...baseRule,
                            id: editingRecord.id,
                            ship_id: shipId
                        }
                    ]
                }));
            }
            // Create mode
            else {
                payloadArray = shipIds.map((shipId) => ({
                    ship_id: shipId,
                    validation_obj: [
                        {
                            ...baseRule,
                            ship_id: shipId
                        }
                    ]
                }));
            }

            console.log("FINAL PAYLOAD >>>", payloadArray);

            await Promise.all(
                payloadArray.map((p) => userService.customModuleSpecific(p))
            );

            message.success(
                editingRecord
                    ? "Validation updated for selected ships"
                    : "Validation created for selected ships"
            );

            fetchValidationRules();
            setModalVisible(false);
            form.resetFields();
            setValidationType(null);
            setEditingRecord(null);
            setSelectedModule(null);

        } catch (error) {
            console.error("Validation rule save error:", error);
            message.error("Failed to save validation rule");
        }
    };

    const handleValidationTypeChange = (value) => {
        setValidationType(value);
        form.setFieldsValue({ minValue: undefined, maxValue: undefined });
    };

    const columns = [
        {
            title: 'Field Name',
            dataIndex: 'field_name',
            key: 'field_name',
            width: 150,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Ship',
            dataIndex: 'ship_id',
            key: 'ship_id',
            width: 150,
            render: (shipId) => {
                const ship = shipOptions.find(s => s.value === shipId);
                return (
                    <Tag color="purple">
                        {ship?.label || `Ship ${shipId}`}
                    </Tag>
                );
            }
        },
        {
            title: 'Type',
            dataIndex: 'field_type',
            key: 'field_type',
            width: 100,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Validation',
            dataIndex: 'validation_type',
            key: 'validation_type',
            width: 130,
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <Tag color="green">{text}</Tag>
                    {record.formula && record.formula !== 'null' && (
                        <Tag color="orange" icon={<FunctionOutlined />} style={{ fontSize: '10px' }}>
                            Has Formula
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Parameters',
            dataIndex: 'validation_parameters',
            key: 'validation_parameters',
            width: 120,
            render: (params) => {
                if (!params) return '-';
                const parsed = typeof params === 'string' ? JSON.parse(params) : params;
                if (parsed.min !== undefined && parsed.max !== undefined) {
                    return <Text type="secondary">{parsed.min} - {parsed.max}</Text>;
                } else if (parsed.min !== undefined) {
                    return <Text type="secondary">≥ {parsed.min}</Text>;
                } else if (parsed.max !== undefined) {
                    return <Text type="secondary">≤ {parsed.max}</Text>;
                }
                return '-';
            }
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, record) => (
                <Tag color={record.isRequired ? 'red' : 'default'}>
                    {record.isRequired ? 'Required' : 'Optional'}
                </Tag>
            ),
        },
        {
            title: 'Error Message',
            dataIndex: 'errormessage',
            key: 'errormessage',
            ellipsis: true,
            width: 200,
            render: (text) => (
                <Text ellipsis style={{ maxWidth: 200 }}>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Delete this rule?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <Row gutter={[16, 8]}>
                    <Col span={8}>
                        <Text strong>ID:</Text> <Text>{record.id}</Text>
                    </Col>
                    <Col span={8}>
                        <Text strong>Updated:</Text> <Text>{new Date(record.updatedAt).toLocaleString()}</Text>
                    </Col>
                    <Col span={8}>
                        <Text strong>Ship ID:</Text> <Text>{record.ship_id}</Text>
                    </Col>
                    {record.formula && record.formula !== 'null' && (
                        <Col span={24}>
                            <Text strong>Formula:</Text>
                            <pre style={{
                                fontSize: '12px',
                                marginTop: '4px',
                                marginBottom: 0,
                                padding: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px'
                            }}>
                                {cleanFormula(record.formula)}
                            </pre>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    const groupedData = groupDataByModule();
 
    return (
        <div >

            <Card size="small" title={<Title level={5} style={{ margin: 0 }}>
                Validation Rules Management
            </Title>}>
                {Object.keys(groupedData).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Text type="secondary">
                            No validation rules found. Create your first rule!
                        </Text>
                    </div>
                ) : (
                    <Collapse
                        defaultActiveKey={Object.keys(groupedData)}
                        expandIcon={({ isActive }) => (
                            <DownOutlined rotate={isActive ? 180 : 0} />
                        )}
                    >
                        {Object.keys(groupedData).map((tableName) => {
                            const moduleInfo = modules.find((m) => m.value === tableName);
                            const moduleRules = groupedData[tableName];
                            const requiredCount = moduleRules.filter((r) => r.isRequired).length;

                            return (
                                <Panel
                                    header={
                                        <Space size="middle">
                                            <strong style={{ fontSize: '15px' }}>
                                                {moduleInfo?.label || tableName}
                                            </strong>
                                            <Tag color="blue">{moduleRules.length} rules</Tag>
                                            {requiredCount > 0 && (
                                                <Tag color="red">{requiredCount} required</Tag>
                                            )}
                                        </Space>
                                    }
                                    key={tableName}
                                    extra={
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAdd(tableName);
                                            }}
                                        >
                                            Add Field
                                        </Button>
                                    }
                                >
                                    <Table
                                        columns={columns}
                                        dataSource={moduleRules}
                                        rowKey={(record) => `${record.id}-${record.ship_id}`}
                                        loading={loading}
                                        pagination={false}
                                        expandable={{
                                            expandedRowRender,
                                        }}
                                        scroll={{ x: 1200 }}
                                        size="small"
                                    />
                                </Panel>
                            );
                        })}
                    </Collapse>
                )}
            </Card>

            <Modal
                title={
                    <Space>
                        {editingRecord ? <EditOutlined /> : <PlusOutlined />}
                        {editingRecord ? 'Edit Validation Rule' : 'Create Validation Rule'}
                    </Space>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setValidationType(null);
                    setEditingRecord(null);
                    setSelectedModule(null);
                }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        isRequired: false,
                        use_previous: false,
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="table_name"
                                label="Module/Table Name"
                                rules={[{ required: true, message: 'Please select a module' }]}
                            >
                                <Select
                                    placeholder="Select module"
                                    disabled={!!selectedModule}
                                >
                                    {modules.map((module) => (
                                        <Option key={module.value} value={module.value}>
                                            {module.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="field_name"
                                label="Field Name"
                                rules={[
                                    { required: true, message: 'Please enter field name' },
                                    {
                                        pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                                        message: 'Invalid field name format',
                                    },
                                ]}
                            >
                                <Input placeholder="e.g., user_email" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="field_type"
                                label="Field Type"
                                rules={[{ required: true, message: 'Please select field type' }]}
                            >
                                <Select placeholder="Select field type">
                                    {fieldTypes.map((type) => (
                                        <Option key={type.value} value={type.value}>
                                            {type.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="ship_ids"
                                label="Ship(s)"
                                rules={[{ required: true, message: 'Please select at least one ship' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select one or more ships"
                                    optionFilterProp="label"
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {shipOptions.map((ship) => (
                                        <Option key={ship.value} value={ship.value} label={ship.label}>
                                            {ship.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="validation_type"
                                label="Validation Type"
                                rules={[{ required: true, message: 'Please select validation type' }]}
                            >
                                <Select
                                    placeholder="Select validation type"
                                    onChange={handleValidationTypeChange}
                                >
                                    {validationTypes.map((type) => (
                                        <Option key={type.value} value={type.value}>
                                            {type.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {(validationType === 'min' || validationType === 'range') && (
                            <Col span={12}>
                                <Form.Item
                                    name="minValue"
                                    label="Minimum Value"
                                    rules={[{ required: true, message: 'Please enter minimum value' }]}
                                >
                                    <InputNumber
                                        placeholder="Enter min value"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        {(validationType === 'max' || validationType === 'range') && (
                            <Col span={12}>
                                <Form.Item
                                    name="maxValue"
                                    label="Maximum Value"
                                    rules={[{ required: true, message: 'Please enter maximum value' }]}
                                >
                                    <InputNumber
                                        placeholder="Enter max value"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item
                                name="errormessage"
                                label="Error Message"
                                rules={[{ required: true, message: 'Please enter error message' }]}
                            >
                                <Input.TextArea
                                    rows={2}
                                    placeholder="Enter custom error message"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="formula"
                                label="Formula (Optional)"
                            >
                                <Input placeholder="e.g., Field1 * 2" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="isRequired"
                                label="Mark as Required"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginBottom: 0, marginTop: '24px', textAlign: 'right' }}>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setModalVisible(false);
                                    form.resetFields();
                                    setValidationType(null);
                                    setEditingRecord(null);
                                    setSelectedModule(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                {editingRecord ? 'Update Rule' : 'Create Rule'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ValidationRulesManagerSimple;